export interface GuideSection {
  title: string;
  items: string[];
}

export interface HowToStep {
  title: string;
  steps: string[];
}

export interface FeatureGuide {
  id: string;
  title: string;
  icon: string;
  description: string;
  canDo: GuideSection;
  cannotDo: GuideSection;
  howTo: HowToStep[];
  tips: string[];
}

export const guides: FeatureGuide[] = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: 'LayoutDashboard',
    description: 'Your real-time overview of restaurant performance and activity.',
    canDo: {
      title: 'What it can do',
      items: [
        'View today\'s revenue, orders, and average ticket size',
        'See real-time table status (available, occupied, reserved)',
        'Monitor which staff are currently clocked in',
        'Track recent orders and their status',
        'Compare performance to previous days (trend indicators)',
        'View hourly sales chart for today',
      ],
    },
    cannotDo: {
      title: 'Current limitations',
      items: [
        'Cannot view historical data beyond today (use Reports for that)',
        'Cannot make changes directly from dashboard (navigate to specific pages)',
        'Does not show projected end-of-day totals',
        'Cannot customize which metrics are displayed',
      ],
    },
    howTo: [
      {
        title: 'Check today\'s performance',
        steps: [
          'Open the Dashboard (click Dashboard in sidebar)',
          'View the stat cards at the top for key metrics',
          'Green/red arrows show comparison to yesterday',
        ],
      },
      {
        title: 'See who\'s working',
        steps: [
          'Scroll to "Staff Currently On Clock" section',
          'View each employee\'s name, role, and hours worked',
        ],
      },
    ],
    tips: [
      'Dashboard data refreshes automatically every 30-60 seconds',
      'Click on staff names to navigate to the Staff page for more details',
      'The hourly sales chart helps identify peak business hours',
    ],
  },
  {
    id: 'menu',
    title: 'Menu',
    icon: 'UtensilsCrossed',
    description: 'Manage your menu items, categories, pricing, and availability.',
    canDo: {
      title: 'What it can do',
      items: [
        'Add, edit, and delete menu items',
        'Set prices, descriptions, and prep times',
        'Organize items into categories',
        'Mark items as 86\'d (unavailable) with one click',
        'Assign kitchen stations for order routing (Grill, Fry, Cold, etc.)',
        'Set short names for kitchen tickets',
        'Filter and search menu items',
      ],
    },
    cannotDo: {
      title: 'Current limitations',
      items: [
        'Cannot auto-deduct inventory when items sell (recipes not linked yet)',
        'Cannot schedule price changes for future dates',
        'Cannot create item modifiers or customizations',
        'Cannot upload item photos',
        'Cannot set time-based availability (happy hour pricing)',
      ],
    },
    howTo: [
      {
        title: 'Add a new menu item',
        steps: [
          'Click "Add Item" button in the top right',
          'Enter the item name (required)',
          'Set a short name for kitchen tickets (optional)',
          'Enter description and price',
          'Select a category',
          'Choose kitchen stations that prepare this item',
          'Click "Add Item" to save',
        ],
      },
      {
        title: '86 an item (mark unavailable)',
        steps: [
          'Find the item in the menu grid',
          'Click the toggle switch on the item card',
          'The item will show "86\'d" badge and be grayed out',
          'Toggle again to make available',
        ],
      },
      {
        title: 'Edit an existing item',
        steps: [
          'Find the item and click "Edit" button',
          'Modify any fields in the form',
          'Click "Save Changes"',
        ],
      },
    ],
    tips: [
      'Use short names to keep kitchen tickets readable (e.g., "Burg" instead of "Classic Cheeseburger")',
      'Assign multiple stations if an item needs prep from different areas',
      'Use the search bar to quickly find items across all categories',
      '86\'d items are still visible to managers but won\'t appear on POS',
    ],
  },
  {
    id: 'inventory',
    title: 'Inventory',
    icon: 'Package',
    description: 'Track stock levels, set par levels, and manage inventory transactions.',
    canDo: {
      title: 'What it can do',
      items: [
        'Track stock quantities with various units (lbs, oz, cases, etc.)',
        'Set par levels for reorder alerts',
        'Record transactions (received shipments, waste, adjustments)',
        'View low stock alerts',
        'See total inventory value',
        'Filter by all items or low stock only',
        'Track unit costs for each item',
      ],
    },
    cannotDo: {
      title: 'Current limitations',
      items: [
        'Cannot auto-deduct when menu items sell (not linked to recipes)',
        'Cannot track expiration dates or lot numbers',
        'Cannot auto-generate purchase orders',
        'Cannot send orders to vendors electronically',
        'Cannot scan barcodes for inventory counts',
        'Cannot track inventory across multiple locations',
      ],
    },
    howTo: [
      {
        title: 'Add a new inventory item',
        steps: [
          'Click "Add Item" button',
          'Enter the item name',
          'Set SKU (optional) and unit of measure',
          'Enter current quantity and par level',
          'Set unit cost for value tracking',
          'Click "Add Item"',
        ],
      },
      {
        title: 'Record received shipment',
        steps: [
          'Find the item in the table',
          'Click the adjust button (up arrow)',
          'Enter the quantity received as a positive number',
          'Select "Received shipment" as the reason',
          'Click "Adjust"',
        ],
      },
      {
        title: 'Record waste or spoilage',
        steps: [
          'Find the item and click adjust',
          'Enter the quantity as a negative number (e.g., -5)',
          'Select "Waste/spoilage" as the reason',
          'Click "Adjust"',
        ],
      },
    ],
    tips: [
      'Set par levels based on your busiest days to avoid running out',
      'Check low stock alerts at the start of each day',
      'Regular inventory counts help catch discrepancies early',
      'Use the "Inventory count" reason when reconciling actual vs. system counts',
    ],
  },
  {
    id: 'staff',
    title: 'Staff',
    icon: 'Users',
    description: 'Manage employees, roles, PINs, and bank connections for payroll.',
    canDo: {
      title: 'What it can do',
      items: [
        'Add new employees with roles and PINs',
        'Set hourly rates for payroll calculation',
        'View who is currently clocked in',
        'Reset employee PINs',
        'Send bank connection invites for direct deposit',
        'View connected bank accounts',
        'Filter by role (Server, Cook, Host, etc.)',
        'Deactivate employees',
      ],
    },
    cannotDo: {
      title: 'Current limitations',
      items: [
        'Cannot schedule shifts in advance',
        'Cannot track tips distribution',
        'Cannot generate tax forms (W-2, 1099)',
        'Cannot set permissions beyond role defaults',
        'Cannot track certifications or training',
        'Cannot manage PTO or time-off requests',
      ],
    },
    howTo: [
      {
        title: 'Add a new employee',
        steps: [
          'Click "Add Staff Member" button',
          'Enter first and last name',
          'Add email and phone (optional but recommended)',
          'Select their role',
          'Set hourly rate',
          'Create a 4-6 digit PIN for POS login',
          'Click "Add Staff Member"',
        ],
      },
      {
        title: 'Set up direct deposit',
        steps: [
          'Find the employee in the table',
          'Click "Connect" in the Bank column',
          'Enter the employee\'s email address',
          'They will receive a link to securely connect their bank',
          'Once connected, status shows "Connected"',
        ],
      },
      {
        title: 'Reset an employee PIN',
        steps: [
          'Click the edit icon next to the employee',
          'Select option 2 (Reset PIN)',
          'Enter a new 4-6 digit PIN',
          'Share the new PIN with the employee',
        ],
      },
    ],
    tips: [
      'Use simple but unique PINs - employees enter these frequently',
      'Bank connections are secure and handled by Plaid',
      'Employees can only access features based on their role',
      'Deactivated employees cannot clock in but history is preserved',
    ],
  },
  {
    id: 'shifts',
    title: 'Shifts',
    icon: 'Clock',
    description: 'Monitor active shifts, manage breaks, and oversee floor operations in real-time.',
    canDo: {
      title: 'What it can do',
      items: [
        'View all employees currently clocked in',
        'See real-time sales, tips, and tables served per employee',
        'Monitor hours worked and break status',
        'Clock out employees remotely (manager override)',
        'Start or end breaks for employees',
        'Reassign employees to different sections mid-shift',
        'Add notes to active shifts',
        'Get break reminder alerts (4+ hours without break)',
      ],
    },
    cannotDo: {
      title: 'Current limitations',
      items: [
        'Cannot clock employees in (they must do pre-shift themselves)',
        'Cannot view historical shift data (use Reports for that)',
        'Cannot edit past shift records',
        'Cannot schedule future shifts',
        'Cannot set break duration limits',
      ],
    },
    howTo: [
      {
        title: 'View active shifts',
        steps: [
          'Navigate to Team > Shifts in the sidebar',
          'See all currently clocked-in employees',
          'Use role filters to narrow the list',
          'Check the stats cards for totals',
        ],
      },
      {
        title: 'Clock out an employee',
        steps: [
          'Find the employee in the shifts table',
          'Click the logout icon in the Actions column',
          'Confirm the clock out action',
          'Employee will be marked as clocked out',
        ],
      },
      {
        title: 'Manage breaks',
        steps: [
          'Find the employee in the shifts table',
          'Click the coffee cup icon to start a break',
          'Icon turns amber when on break',
          'Click again to end the break',
        ],
      },
      {
        title: 'Reassign a section',
        steps: [
          'Find the employee with a section assignment',
          'Click the dropdown arrow next to their section name',
          'Select the new section from the list',
          'Tables will be reassigned automatically',
        ],
      },
      {
        title: 'Add a shift note',
        steps: [
          'Click the message icon in the Actions column',
          'Type your note in the modal',
          'Click Add Note to save',
          'Notes are visible in shift records',
        ],
      },
    ],
    tips: [
      'Check the break reminder alert at the top for employees needing breaks',
      'Data refreshes automatically every 30 seconds',
      'Click Refresh for immediate updates during busy service',
      'Use section reassignment to balance the floor dynamically',
      'Manager clock-outs are logged for accountability',
    ],
  },
  {
    id: 'reservations',
    title: 'Reservations',
    icon: 'Calendar',
    description: 'Create and manage table reservations with status tracking.',
    canDo: {
      title: 'What it can do',
      items: [
        'Create new reservations with guest details',
        'View reservations by date (7-day view)',
        'Filter by status (pending, confirmed, seated, completed)',
        'Confirm pending reservations',
        'Seat guests at available tables',
        'Cancel reservations',
        'Track party size and contact info',
        'Add notes to reservations',
      ],
    },
    cannotDo: {
      title: 'Current limitations',
      items: [
        'Cannot accept online reservations (manual entry only)',
        'Cannot set table preferences when booking',
        'Cannot send automated confirmation emails/texts',
        'Cannot manage waitlists',
        'Cannot set reservation time limits',
        'Cannot block specific times or dates',
      ],
    },
    howTo: [
      {
        title: 'Create a reservation',
        steps: [
          'Click "New Reservation" button',
          'Enter guest name (required)',
          'Add phone and email for follow-up',
          'Set party size, date, and time',
          'Add any notes (allergies, special requests)',
          'Click "Create Reservation"',
        ],
      },
      {
        title: 'Confirm a pending reservation',
        steps: [
          'Find the reservation in the list',
          'Click the green checkmark button',
          'Status changes to "Confirmed"',
        ],
      },
      {
        title: 'Seat a confirmed reservation',
        steps: [
          'Find the confirmed reservation',
          'Click "Seat" button',
          'Enter the table number from available tables',
          'Status changes to "Seated"',
        ],
      },
    ],
    tips: [
      'Use the date tabs to quickly see upcoming reservations',
      'Add phone numbers to call guests who are late',
      'Notes are visible to hosts and servers',
      'Completed reservations show visit duration for future reference',
    ],
  },
  {
    id: 'guests',
    title: 'Guests',
    icon: 'Contact',
    description: 'Customer profiles with visit history, preferences, and VIP tracking.',
    canDo: {
      title: 'What it can do',
      items: [
        'View customer profiles with visit history',
        'Track total visits and spending per guest',
        'Mark guests as VIP for special treatment',
        'Record allergies and dietary restrictions',
        'Add notes about guest preferences',
        'See loved and disliked menu items',
        'Filter to show VIP guests only',
        'Search by name, email, or phone',
      ],
    },
    cannotDo: {
      title: 'Current limitations',
      items: [
        'Cannot send marketing emails or texts to guests',
        'Cannot create loyalty programs or points',
        'Cannot merge duplicate guest profiles',
        'Cannot import guests from other systems',
        'Cannot track gift card balances',
        'Cannot set birthday/anniversary auto-reminders',
      ],
    },
    howTo: [
      {
        title: 'View guest details',
        steps: [
          'Search for the guest or browse the list',
          'Click on any row to open the detail panel',
          'View stats, contact info, preferences, and visit history',
        ],
      },
      {
        title: 'Mark a guest as VIP',
        steps: [
          'Open the guest detail panel',
          'Click "Mark as VIP" button at the bottom',
          'A star icon will appear next to their name',
          'VIP guests can be filtered for special attention',
        ],
      },
      {
        title: 'Add a note about a guest',
        steps: [
          'Open the guest detail panel',
          'Type in the notes field at the bottom',
          'Press Enter or click the send button',
          'Notes are appended to the guest profile',
        ],
      },
      {
        title: 'Record allergies',
        steps: [
          'When adding a new guest, enter allergies comma-separated',
          'For existing guests, this displays but cannot be edited yet',
          'Allergy warnings appear on the guest list',
        ],
      },
    ],
    tips: [
      'Guest profiles are created automatically from reservations and POS',
      'Use VIP status for regulars who deserve extra attention',
      'Check guest allergies before seating to alert the kitchen',
      'Average check size helps identify high-value customers',
    ],
  },
  {
    id: 'ai-chat',
    title: 'AI Chat Assistant',
    icon: 'MessageSquare',
    description: 'Natural language assistant for quick tasks and questions.',
    canDo: {
      title: 'What it can do',
      items: [
        'Answer questions about today\'s performance',
        'Check and update inventory quantities',
        'Find low stock items',
        '86 menu items by name',
        'Look up guest information',
        'Create reservations via conversation',
        'Provide quick stats and summaries',
        'Help navigate to the right page',
      ],
    },
    cannotDo: {
      title: 'Current limitations',
      items: [
        'Cannot process payments or refunds',
        'Cannot add or remove staff members',
        'Cannot change restaurant settings',
        'Cannot access detailed historical reports',
        'Cannot upload files or images',
        'Cannot connect to external systems',
      ],
    },
    howTo: [
      {
        title: 'Open the chat assistant',
        steps: [
          'Click the chat bubble icon in the bottom right corner',
          'The chat panel will slide open',
          'Type your question or command',
        ],
      },
      {
        title: 'Ask about performance',
        steps: [
          'Try: "How are we doing today?"',
          'Or: "What\'s our revenue so far?"',
          'Or: "How many orders have we had?"',
        ],
      },
      {
        title: '86 an item quickly',
        steps: [
          'Type: "86 the salmon" or "Mark salmon as unavailable"',
          'The assistant will confirm the action',
        ],
      },
      {
        title: 'Check inventory',
        steps: [
          'Ask: "What\'s running low?"',
          'Or: "How much chicken do we have?"',
          'Or: "Update olive oil to 5 gallons"',
        ],
      },
    ],
    tips: [
      'Use natural language - no special commands needed',
      'The assistant knows the context of the current page',
      'Great for hands-free updates during busy service',
      'Ask "What can you help with?" to see capabilities',
    ],
  },
  {
    id: 'settings',
    title: 'Settings',
    icon: 'Settings',
    description: 'Configure restaurant info, hours, floor plan, and system settings.',
    canDo: {
      title: 'What it can do',
      items: [
        'Update restaurant name, address, and contact info',
        'Set operating hours for each day',
        'Configure floor plan with sections and tables',
        'Set tax rate and tip options',
        'Manage notification preferences',
        'View POS/KDS login code',
        'Configure printer settings',
        'Set auto clock-out time',
      ],
    },
    cannotDo: {
      title: 'Current limitations',
      items: [
        'Cannot customize staff role permissions',
        'Cannot enable two-factor authentication (coming soon)',
        'Cannot set multiple tax rates',
        'Cannot configure online ordering settings',
        'Cannot white-label or customize branding',
      ],
    },
    howTo: [
      {
        title: 'Set up your floor plan',
        steps: [
          'Go to Settings > Floor Plan tab',
          'Click "Add Section" to create areas (Main Dining, Patio, etc.)',
          'Click on the canvas to add tables to each section',
          'Drag tables to position them',
          'Set table numbers and capacities',
        ],
      },
      {
        title: 'Connect POS or KDS devices',
        steps: [
          'Go to Settings > Devices tab',
          'Copy the Restaurant Login Code',
          'On your POS/KDS device, enter the code',
          'Staff can then log in with their PINs',
        ],
      },
      {
        title: 'Update operating hours',
        steps: [
          'Go to Settings > Hours tab',
          'Set open and close times for each day',
          'Uncheck "Open" for days you\'re closed',
          'Click "Save Changes"',
        ],
      },
    ],
    tips: [
      'Keep your restaurant info updated for accurate receipts',
      'Use sections to organize tables logically for servers',
      'The login code is case-sensitive',
      'Enable low stock alerts to avoid running out during service',
    ],
  },
];

export const getGuideById = (id: string): FeatureGuide | undefined => {
  return guides.find((guide) => guide.id === id);
};

export const getGuidesByCategory = () => {
  return {
    operations: guides.filter((g) => ['dashboard', 'menu', 'inventory'].includes(g.id)),
    customers: guides.filter((g) => ['reservations', 'guests'].includes(g.id)),
    team: guides.filter((g) => ['staff', 'shifts'].includes(g.id)),
    tools: guides.filter((g) => ['ai-chat', 'settings'].includes(g.id)),
  };
};
